import { tableService } from './tableService'
import { apiService, ApiResponse } from './apiService'
import type { SearchOptions } from '../types/api.types'

/**
 * Data Service
 * Generic service for querying regular business data tables
 *
 * Use this for tables like offerings, customers, currencies, uoms, etc.
 * For pipelines and blobs, use their specific services (different endpoint pattern).
 */
export class DataService {
  /**
   * Search any data table with filters, sorting, and pagination
   *
   * @param tableName - Table name (e.g., 'offerings', 'customers', 'currencies')
   * @param options - Search options
   * @returns API response with matching records
   *
   * @example
   * // Search offerings
   * const response = await dataService.search('offerings', {
   *   filters: [
   *     { key: 'deletedAt', constraint_type: 'IS_NULL' },
   *     { key: 'price', constraint_type: 'GT', value: 100 }
   *   ],
   *   order_by: [{ key: 'name', direction: 'asc' }],
   *   limit: 50
   * })
   */
  async search<T>(
    tableName: string,
    options?: SearchOptions
  ): Promise<ApiResponse<T[]>> {
    return await tableService.search<T>(`/${tableName}`, options)
  }

  /**
   * Get a single record by UID
   *
   * @param tableName - Table name
   * @param uid - Record UID
   * @returns Single record or null if not found
   */
  async get<T>(tableName: string, uid: string): Promise<ApiResponse<T>> {
    return await apiService.get<T>(`/${tableName}/${uid}`)
  }

  /**
   * Create a new record
   *
   * @param tableName - Table name
   * @param data - Record data
   * @returns Created record with generated UID
   */
  async create<T>(tableName: string, data: Partial<T>): Promise<ApiResponse<T>> {
    return await apiService.post<T>(`/${tableName}`, data)
  }

  /**
   * Update an existing record with optimistic locking
   *
   * @param tableName - Table name
   * @param uid - Record UID
   * @param data - Fields to update (include 'version' field for optimistic locking)
   * @returns Updated record
   */
  async update<T>(
    tableName: string,
    uid: string,
    data: Partial<T>
  ): Promise<ApiResponse<T>> {
    // version should be included in data for optimistic locking (backend extracts it)
    return await apiService.put<T>(`/${tableName}/${uid}`, data)
  }

  /**
   * Soft delete a record
   *
   * @param tableName - Table name
   * @param uid - Record UID
   */
  async delete(tableName: string, uid: string): Promise<ApiResponse<void>> {
    return await apiService.delete<void>(`/${tableName}/${uid}`)
  }
}

// Export singleton instance
export const dataService = new DataService()
export default dataService
